import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import isMobile from 'ismobilejs';

import { sendMessage, resetUnreadCount } from '../actions/conversation';
import { startTyping, stopTyping } from '../actions/typing';
import { hideUpload, setUserIsTyping } from '../actions/app-state';

import UploadButton from './UploadButton';
import { FOOTER } from '../constants/dimensions';
import { IOS_SCALE_STYLING } from '../constants/styles';

import { bindAll } from '../utils/functions';
import { updateText } from '../actions/ui';

const disabledBackgroundColor = '#f4f6f8';

const styles = {
    shared: {
        transitionDuration: '0.3s',
        transitionTimingFunction: 'ease-out',
        transitionProperty: 'color, background-color, border-color, opacity',
    },
    textArea: {
        resize: 'none',
    },
    disabled: {
        backgroundColor: disabledBackgroundColor,
        borderColor: disabledBackgroundColor,
        color: '#9ba4b3',
        cursor: 'not-allowed',
    },
    disabledButton: {
        color: '#b3b3b3',
        opacity: 0.5,
    },
    isAppleDevice: IOS_SCALE_STYLING.THIRTEEN_PIXELS,
};

export class ChatInputComponent extends Component {
    static propTypes = {
        accentColor: PropTypes.string,
        menuItems: PropTypes.object.isRequired,
        inputPlaceholderBlockedText: PropTypes.string.isRequired,
        inputPlaceholderText: PropTypes.string.isRequired,
        disabled: PropTypes.bool,
        sendButtonText: PropTypes.string.isRequired,
        startingText: PropTypes.string,
        unreadCount: PropTypes.number.isRequired,
        dispatch: PropTypes.func.isRequired,
        footerRef: PropTypes.object.isRequired,
    };

    static defaultProps = {
        disabled: false,
    };

    state = {
        text: this.props.startingText || '',
    };

    constructor(...args) {
        super(...args);
        bindAll(
            this,
            'blur',
            'checkAndResetUnreadCount',
            'onChange',
            'onFocus',
            'onBlur',
            'onSendMessage',
            'updateStartingText',
        );
    }

    componentDidUpdate() {
        this.updateStartingText();
    }

    blur() {
        this._input.blur();
    }

    onBlur() {
        this.props.dispatch(stopTyping());
    }

    checkAndResetUnreadCount(unreadCount) {
        const { dispatch } = this.props;
        if (unreadCount > 0) {
            dispatch(resetUnreadCount());
        }
    }

    onChange(e) {
        const text = e.target.value;
        this.checkAndResetUnreadCount(this.props.unreadCount);
        this.setState({
            text,
        });

        if (text.trim()) {
            this.props.dispatch(startTyping());
        }
    }

    onFocus() {
        this.props.dispatch(hideUpload());
        this.checkAndResetUnreadCount(this.props.unreadCount);
    }

    onSendMessage(e) {
        e.preventDefault();
        const { text } = this.state;
        const { dispatch } = this.props;

        this.props.dispatch(setUserIsTyping(false));

        if (text.trim()) {
            dispatch(sendMessage(text));
            dispatch(hideUpload());

            this._input.value = '';
            this.setState({
                text: '',
            });

            this._input.focus();
        }
    }

    updateStartingText() {
        if (this.props.startingText !== undefined) {
            const { startingText } = this.props;
            this.props.dispatch(updateText({ startingText: undefined }));
            this.setState({
                text: startingText,
            });
        }
    }

    onKeyPress(e) {
        if (!e.shiftKey && e.key === 'Enter') {
            this.onSendMessage(e);
        }
    }

    _getRows() {
        const rowsBefore = this._input.rows;

        this._input.rows = 1;
        const rows = Math.ceil((this._input.scrollHeight - FOOTER.BASE_HEIGHT) / FOOTER.INPUT_LINE_HEIGHT) + 1;

        this._input.rows = rowsBefore;

        // IE sometimes miscalculates rows to be 0 and crashes the SDK
        return Math.max(rows, 1);
    }

    render() {
        const {
            accentColor,
            menuItems,
            inputPlaceholderBlockedText,
            inputPlaceholderText,
            disabled,
            sendButtonText,
            footerRef,
        } = this.props;

        let sendButton;

        const buttonClassNames = ['send'];
        const buttonStyle = { ...styles.shared };
        const uploadButtonEnabled = menuItems.imageUpload || menuItems.fileUpload || menuItems.shareLocation;

        if (this.state.text.trim() && !disabled) {
            buttonClassNames.push('active');

            if (accentColor) {
                buttonStyle.color = `#${accentColor}`;
            }
        }

        if (disabled) {
            Object.assign(buttonStyle, styles.disabled, styles.disabledButton);
        }

        const isMobileDevice = isMobile();
        const isAppleMobile = isMobileDevice.apple.device;

        if (isAppleMobile) {
            // Safari on iOS needs a way to send on click, without triggering a mouse event.
            // onTouchStart will do the trick and the input won't lose focus.
            sendButton = (
                <span
                    ref={(c) => (this._button = c)}
                    className={buttonClassNames.join(' ')}
                    disabled={disabled}
                    onTouchStart={!disabled ? this.onSendMessage : null}
                    style={buttonStyle}
                >
                    {sendButtonText}
                </span>
            );
        } else {
            sendButton = (
                <a
                    ref={(c) => (this._button = c)}
                    className={buttonClassNames.join(' ')}
                    disabled={disabled}
                    onClick={!disabled ? this.onSendMessage : null}
                    style={buttonStyle}
                >
                    {sendButtonText}
                </a>
            );
        }

        const imageUploadButton = uploadButtonEnabled ? (
            <UploadButton
                ref={(c) => (this._imageUpload = c)}
                disabled={disabled}
                color={disabled ? styles.disabledButton.color : undefined}
            />
        ) : null;

        const inputContainerClasses = ['input-container'];

        if (!uploadButtonEnabled) {
            inputContainerClasses.push('no-upload');
        }

        let isSingleLine = true;
        let isScrollable = false;

        if (this._input) {
            const rows = this._getRows();
            isSingleLine = rows === 1;
            isScrollable = rows > 5;

            this._input.rows = Math.min(rows, 5);
        }

        const extraClasses = [];
        isSingleLine && extraClasses.push('single-line');
        isScrollable && extraClasses.push('scrollable');

        const className = `input message-input ${extraClasses.join(' ')}`;

        const textAreaStyles = {
            ...styles.shared,
            ...styles.textArea,
            ...(disabled && styles.disabled),
            ...(isAppleMobile && styles.isAppleDevice),
        };

        return (
            <div
                id="footer"
                ref={footerRef}
                style={
                    disabled ? { ...styles.shared, backgroundColor: styles.disabled.backgroundColor } : styles.shared
                }
            >
                {imageUploadButton}
                <form onSubmit={this.onSendMessage} action="#">
                    <div className={inputContainerClasses.join(' ')}>
                        <textarea
                            ref={(c) => (this._input = c)}
                            rows={1}
                            onBlur={this.onBlur}
                            className={className}
                            onFocus={this.onFocus}
                            title={sendButtonText}
                            value={this.state.text}
                            style={textAreaStyles}
                            onChange={this.onChange}
                            placeholder={disabled ? inputPlaceholderBlockedText : inputPlaceholderText}
                            disabled={disabled}
                            onKeyPress={!isMobileDevice.any ? this.onKeyPress.bind(this) : undefined}
                        />
                    </div>
                </form>
                {sendButton}
            </div>
        );
    }
}

export default connect(
    ({ appState, ui: { text }, config, conversations, appState: { activeConversationId } }) => {
        const { unreadCount } = conversations[activeConversationId];
        return {
            accentColor: config.colors.accentColor,
            disabled: appState.blockChatInput,
            inputPlaceholderText: text.inputPlaceholder,
            inputPlaceholderBlockedText: text.inputPlaceholderBlocked,
            menuItems: config.menuItems,
            sendButtonText: text.sendButtonText,
            startingText: text.startingText,
            unreadCount,
        };
    },
    undefined,
    undefined,
    {
        forwardRef: true,
    },
)(ChatInputComponent);
